import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/core';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { RICH_TEXT_BAR_ID } from '@bay-email/editor';
import { TiptapTools } from './components/Tools/TiptapTools';
import styleText from './shadow-dom.scss?inline';

export interface FloatingToolbarProps {
	editor: Editor;
	// The ProseMirror root of the active editable.
	anchor: HTMLElement;
}

// Anchored to the editable rather than the selection: caret-only commands
// (alignment, lists, Dynamic Fields) need a bar when nothing is selected.
export function FloatingToolbar({ editor, anchor }: FloatingToolbarProps) {
	const { refs, floatingStyles } = useFloating({
		placement: 'top',
		strategy: 'fixed',
		middleware: [offset(8), flip(), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate,
		elements: { reference: anchor },
	});

	return createPortal(
		<>
			<style dangerouslySetInnerHTML={{ __html: styleText }} />
			<div
				id={RICH_TEXT_BAR_ID}
				className='bay-email-rich-text-bar bay-email-rich-text-bar--light'
				ref={refs.setFloating}
				style={{
					...floatingStyles,
					// Portals to body, so it must clear a host modal (Office's campaign wizard is a Bootstrap modal at 1055)
					zIndex: 'var(--bs-zindex-portal-popover, 1110)',
					padding: '4px 6px',
					boxSizing: 'border-box',
					backgroundColor: '#fff',
					border: '1px solid #e5e6eb',
					borderRadius: 10,
					boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
					maxWidth: 'calc(100vw - 16px)',
				}}
			>
				<TiptapTools editor={editor} />
			</div>
		</>,
		document.body,
	);
}
